import { useCallback } from "react";
import { useAuth as useOidcAuth } from "react-oidc-context";
import { useAppDispatch } from "@/store/hooks";
import { logout } from "@/store/authSlice";
import { clearUser } from "@/store/userSlice";
import { AuthService } from "@/service/AuthService";
import Logger from "@/utils/Logger";

export const useAuthActions = () => {
  const oidcAuth = useOidcAuth();
  const dispatch = useAppDispatch();

  const signIn = useCallback(async () => {
    try {
      Logger.info("Initiating sign-in", ["useAuthActions"]);
      await AuthService.performSignIn(oidcAuth.signinRedirect);
    } catch (error) {
      Logger.error("Sign-in failed", error, ["useAuthActions"]);
      throw error;
    }
  }, [oidcAuth.signinRedirect]);

  const signOut = useCallback(async () => {
    try {
      Logger.info("User initiated sign-out", ["useAuthActions"]);

      // Save path before logout (user might want to return)
      AuthService.savePostLoginRedirect();

      dispatch(logout());
      dispatch(clearUser());

      // Clear flag to prevent double consumption of redirect
      AuthService.resetRedirectConsumption();

      await AuthService.performSignOut(oidcAuth.signoutRedirect);
    } catch (error) {
      Logger.error("Sign-out failed", error, ["useAuthActions"]);
      throw error;
    }
  }, [oidcAuth.signoutRedirect, dispatch]);

  const signInSilent = useCallback(async () => {
    try {
      Logger.info("Attempting silent sign-in", ["useAuthActions"]);
      await AuthService.performSilentSignIn(oidcAuth.signinSilent);
    } catch (error) {
      Logger.error("Silent sign-in failed", error, ["useAuthActions"]);

      // Save path before re-auth
      AuthService.savePostLoginRedirect();
      throw error;
    }
  }, [oidcAuth.signinSilent]);

  const handleUnauthorized = useCallback(() => {
    Logger.warn("Handling unauthorized - forcing re-authentication", ["useAuthActions"]);

    // Clear Redux state immediately to prevent loops
    dispatch(logout());
    dispatch(clearUser());

    // Force redirect to login (will save path and clear stale state)
    AuthService.forceLoginRedirect(oidcAuth.signinRedirect);
  }, [oidcAuth.signinRedirect, dispatch]);

  return {
    signIn,
    signOut,
    signInSilent,
    handleUnauthorized,
  };
};